'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Skill {
    id: number;
    name: string;
    level: number;
}

interface Role {
    id: number;
    title: string;
    description: string;
    skills: Skill[];
    icon: string;
    color: string;
}

const SkillsRoleMapping: React.FC = () => {
    const [selectedRole, setSelectedRole] = useState<number>(1);

    const roles: Role[] = [
        {
            id: 1,
            title: 'Full Stack Developer',
            description: 'Build complete web applications from database to user interface',
            icon: 'CodeBracketIcon',
            color: 'bg-primary',
            skills: [
                { id: 1, name: 'React & Next.js', level: 90 },
                { id: 2, name: 'Node.js & Express', level: 85 },
                { id: 3, name: 'Database Design', level: 80 },
                { id: 4, name: 'REST APIs', level: 88 }
            ]
        },
        {
            id: 2,
            title: 'Data Scientist',
            description: 'Analyze data and build machine learning models to solve business problems',
            icon: 'ChartBarIcon',
            color: 'bg-accent',
            skills: [
                { id: 5, name: 'Python & Pandas', level: 92 },
                { id: 6, name: 'Machine Learning', level: 85 },
                { id: 7, name: 'Data Visualization', level: 88 },
                { id: 8, name: 'SQL & Databases', level: 90 }
            ]
        },
        {
            id: 3,
            title: 'UI/UX Designer',
            description: 'Create intuitive and beautiful user experiences for digital products',
            icon: 'PaintBrushIcon',
            color: 'bg-secondary',
            skills: [
                { id: 9, name: 'Figma & Design Tools', level: 95 },
                { id: 10, name: 'User Research', level: 88 },
                { id: 11, name: 'Prototyping', level: 90 },
                { id: 12, name: 'Design Systems', level: 85 }
            ]
        },
        {
            id: 4,
            title: 'Mobile Developer',
            description: 'Build native and cross-platform mobile applications',
            icon: 'DevicePhoneMobileIcon',
            color: 'bg-warning',
            skills: [
                { id: 13, name: 'React Native', level: 90 },
                { id: 14, name: 'Mobile UI/UX', level: 88 },
                { id: 15, name: 'App Performance', level: 85 },
                { id: 16, name: 'Push Notifications', level: 82 }
            ]
        }
    ];

    const currentRole = roles.find(role => role.id === selectedRole) || roles[0];

    return (
        <section className="py-16 bg-surface">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
                        Your Path to Any Tech Role
                    </h2>
                    <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                        Discover the skills you need to master for your dream career and track your progress
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Role Selection */}
                    <div className="lg:col-span-1">
                        <h3 className="text-xl font-semibold text-text-primary mb-4">Choose Your Role</h3>
                        <div className="space-y-3">
                            {roles.map((role) => (
                                <button
                                    key={role.id}
                                    onClick={() => setSelectedRole(role.id)}
                                    className={`w-full text-left p-4 rounded-lg transition-all duration-300 ${selectedRole === role.id
                                            ? 'glass glass-border shadow-lg'
                                            : 'bg-background hover:bg-surface border border-border'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-12 h-12 rounded-lg ${role.color} flex items-center justify-center`}>
                                            <Icon name={role.icon} size={24} variant="solid" className="text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-text-primary">{role.title}</h4>
                                            <p className="text-sm text-text-tertiary line-clamp-1">{role.description}</p>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Skills Breakdown */}
                    <div className="lg:col-span-2">
                        <div className="glass glass-border rounded-lg p-8">
                            <div className="flex items-start gap-4 mb-8">
                                <div className={`w-16 h-16 rounded-lg ${currentRole.color} flex items-center justify-center flex-shrink-0`}>
                                    <Icon name={currentRole.icon} size={32} variant="solid" className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-text-primary mb-2">{currentRole.title}</h3>
                                    <p className="text-text-secondary">{currentRole.description}</p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <h4 className="text-lg font-semibold text-text-primary mb-4">Required Skills</h4>
                                {currentRole.skills.map((skill) => (
                                    <div key={skill.id} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-text-primary font-medium">{skill.name}</span>
                                            <span className="text-text-tertiary text-sm">{skill.level}%</span>
                                        </div>
                                        <div className="h-2 bg-border rounded-full overflow-hidden">
                                            <div
                                                className={`h-full ${currentRole.color} transition-all duration-500`}
                                                style={{ width: `${skill.level}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-8 pt-6 border-t border-border">
                                <button className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-hover transition-smooth focus-ring">
                                    Start Learning Path
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SkillsRoleMapping;